<template>
    <div>

        <div class="my-6 flex justify-between items-center">
            <h2
                class="text-3xl font-semibold text-gray-700 dark:text-gray-200"
            >
                Quote Definition
            </h2>
            <jet-button v-if="quote" @click.prevent="showQuotePreviewModal=true"
                        class="px-10 py-4 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
            >
                Preview Quote
            </jet-button>
        </div>
        <h4 class="mb-4 text-2xl font-bold text-gray-600 dark:text-gray-300">
            Quote Title
        </h4>
        <label class="block text-sm mb-8">
            <jet-input
                type="text"
                class="block w-full mt-1 text-sm dark:border-gray-600 dark:bg-gray-700 focus:border-red-400 focus:outline-none focus:shadow-outline-red dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                placeholder="Add Title"
                v-model="form.name"
                @change.prevent="saveQuote"
            />
        </label>
        <h5 class="mb-4 text-md font-bold text-gray-600 dark:text-gray-300">
            Quote theme
        </h5>
        <label class="block text-sm mb-8">
            <jet-select v-model:selected="form.theme_source_folder_name" @change.prevent="saveQuote">
                <option :value="null" disabled>Select theme</option>
                <option v-for="(theme, theme_folder) in quoteThemes" :key="'theme-' + theme_folder" :value="theme_folder">{{ theme }}</option>
            </jet-select>
        </label>

        <div v-if="!form.sections.length" class="section min-w-0 mb-10 bg-white rounded-lg shadow-xs dark:bg-gray-800">
            <div class="new-section px-4 py-6">
                <div class="flex flex-col items-center text-center">
                    <p class="mb-2 text-lg font-semibold text-gray-600 dark:text-gray-300">
                        To get started, add a Section.
                    </p>
                    <p class="mb-6 text-gray-600 dark:text-gray-400">
                        The building process always starts with a section, then subsections, then blocks.
                    </p>
                    <div class="flex items-center">
                        <jet-button
                            @click.prevent="addSection(form.sections, sectionTypeWrapper)"
                            class="flex items-center justify-between px-5 py-3 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                        >
                            <span class="mr-2" aria-hidden="true">+</span>
                            <span>New Wrapper Section</span>
                        </jet-button>

                        <jet-button
                            @click.prevent="addSection(form.sections, sectionTypeContent)"
                            class="ml-6 flex items-center justify-between px-5 py-3 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                        >
                            <span class="mr-2" aria-hidden="true">+</span>
                            <span>New Content Section</span>
                        </jet-button>
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <draggable :list="form.sections"
                       item-key="uuid"
                       handle=".section-handle"
                       @change="saveQuote"
            >
                <template #item="{element, index}">
                    <quote-section-editor :client="client"
                                          :quote="quote"
                                          v-model:section="form.sections[index]"
                                          @remove-self="removeSection(form.sections, index)"
                                          @cloneSelf="cloneSection(element, form.sections)"
                                          @open-block-type-modal="openBlockTypeModal"
                                          @open-block-preview-modal="openBlockPreviewModal"
                                          @open-block-edit-modal="openBlockEditModal"
                                          @save-quote="saveQuote"
                    >
                    </quote-section-editor>
                </template>
            </draggable>

            <div class="flex items-center">
                <jet-button
                    @click.prevent="addSection(form.sections, sectionTypeWrapper)"
                    class="flex items-center justify-between px-5 py-3 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                >
                    <span class="mr-2" aria-hidden="true">+</span>
                    <span>New Wrapper Section</span>
                </jet-button>

                <jet-button
                    @click.prevent="addSection(form.sections, sectionTypeContent)"
                    class="ml-6 flex items-center justify-between px-5 py-3 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                >
                    <span class="mr-2" aria-hidden="true">+</span>
                    <span>New Content Section</span>
                </jet-button>
            </div>
        </template>

        <!-- Add Sections -->


        <div class="w-full flex">
            <jet-button @click.prevent="finishEditing(false)"
                        :class="{
                            'opacity-25': form.processing,
                            'mr-2 w-1/2': !isTemplate && canBeSubmitted,
                            'w-full': isTemplate || !canBeSubmitted,
                         }"
                        class="block mt-8 mx-auto px-10 py-4 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                        :disabled="form.processing">
                Save Quote
            </jet-button>
            <jet-button v-if="!isTemplate && canBeSubmitted" @click.prevent="finishEditing(true)"
                        :class="{ 'opacity-25': form.processing }"
                        class="block ml-2 w-1/2 mt-8 mx-auto px-10 py-4 font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                        :disabled="form.processing">
                Save and Submit/Resubmit Quote
            </jet-button>
        </div>

        <quote-block-type-modal
            @close="closeModal('showBlockTypeSelectModal')"
            @templateSelected="templateSelected"
            :is-modal-open="showBlockTypeSelectModal"
        ></quote-block-type-modal>
        <quote-block-preview-modal v-if="showBlockPreviewModal"
                                   :quote="quote"
                                   :is-modal-open="showBlockPreviewModal"
                                   @close="closeModal('showBlockPreviewModal')"
                                   :element="previewElement"
        ></quote-block-preview-modal>
        <quote-block-edit-modal v-if="showEditBlockModal"
                                @close="closeModal('showEditBlockModal')"
                                :is-modal-open="showEditBlockModal"
                                :client="client"
                                :quote="quote"
                                :element="editElement"
                                @html-changed="updateBlock"
        ></quote-block-edit-modal>
        <quote-preview-modal v-if="showQuotePreviewModal"
                             @close="closeModal('showQuotePreviewModal')"
                             :is-modal-open="showQuotePreviewModal"
                             :client="client"
                             :quote="quote"
        ></quote-preview-modal>
    </div>
</template>

<script>
import JetInput from '@/Jetstream/Input';
import JetButton from '@/Jetstream/Button';
import JetLabel from '@/Jetstream/Label';
import JetFormCard from '@/Jetstream/FormCard';
import JetActionMessage from '@/Jetstream/ActionMessage.vue'
import JetInputError from '@/Jetstream/InputError';
import QuoteBlockTypeModal from "@/Pages/Admin/Quote/Partials/QuoteBlockTypeModal";
import QuoteBlockPreviewModal from "@/Pages/Admin/Quote/Partials/QuoteBlockPreviewModal";
import {useForm} from "@inertiajs/inertia-vue3";
import QuoteSectionEditor from "@/Pages/Admin/Quote/Partials/QuoteSectionEditor";
import {useSections} from "@/includes/useSections";
import Draggable from "vuedraggable";
import QuoteBlockEditModal from "@/Pages/Admin/Quote/Partials/QuoteBlockEditModal";
import useEditBlocksHTMLModal from "@/includes/useEditBlocksHTMLModal";
import useBlockPreviewModal from "@/includes/useBlockPreviewModal";
import useBlockTypeSelectModal from "@/includes/useBlockTypeSelectModal";
import QuotePreviewModal from "@/Pages/Admin/Quote/Partials/QuotePreviewModal";
import {computed, inject, ref} from "vue";
import JetSelect from "@/Jetstream/Select";

export default {
    name: "QuoteEditor",
    components: {
        QuotePreviewModal,
        QuoteBlockEditModal,
        QuoteSectionEditor,
        JetInput,
        JetLabel,
        JetButton,
        JetFormCard,
        JetActionMessage,
        JetInputError,
        JetSelect,
        QuoteBlockTypeModal,
        QuoteBlockPreviewModal,
        Draggable
    },
    props: {
        allTags: {
            type: Array,
            default: [],
        },
        client: {
            type: Object,
            default: null,
        },
        quote: {
            type: Object,
            required: false,
        },
        quoteThemes: {
            type: Object,
            required: true,
        },
        isTemplate: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const form = useForm({
            name: props.quote?.name ?? 'New quote' + (props.isTemplate ? ' template' : ''),
            theme_source_folder_name: props.quote?.theme_source_folder_name ?? 'wireframe',
            sections: props.quote?.sections ?? [],
        });

        const saveQuote = (publish = false, closeEditorAfterSave = false) => {
            let saveRoute;
            //set default value to allow calling saveQuote on event handles without params
            //if we don't set values, the function will be sent the event as the first parameter
            if (publish !== true) {
                publish = false;
            }
            if (closeEditorAfterSave !== true) {
                closeEditorAfterSave = false;
            }

            if (props.isTemplate) {
                saveRoute = !props.quote
                    ? route('admin.quote-template.store')
                    : route('admin.quote-template.update', {quote_template: props.quote.id});
            } else {
                saveRoute = !props.quote
                    ? route('admin.quote.store', {client: props.client.id})
                    : route('admin.quote.update', {client: props.client.id, quote: props.quote.id});
            }

            form.transform((data) => ({
                ...data,
                publish,
                redirect_to_index: closeEditorAfterSave,
            }));

            if (!props.quote) {
                form.post(saveRoute);
            } else {
                form.patch(saveRoute);
            }
        };

        const {
            sectionTypeWrapper,
            sectionTypeContent,
            addSection,
            removeSection,
            cloneSection
        } = useSections(saveQuote);

        const {showBlockTypeSelectModal, openBlockTypeModal, templateSelected} = useBlockTypeSelectModal();

        const {showBlockPreviewModal, previewElement, openBlockPreviewModal} = useBlockPreviewModal();

        const {showEditBlockModal, editElement, openBlockEditModal, updateBlock} = useEditBlocksHTMLModal();

        const closeModal = (modal) => {
            switch (modal) {
                case 'showBlockTypeSelectModal': {
                    showBlockTypeSelectModal.value = false;
                    break;
                }
                case 'showBlockPreviewModal': {
                    showBlockPreviewModal.value = false;
                    break;
                }
                case 'showEditBlockModal': {
                    showEditBlockModal.value = false;
                    break;
                }
                case 'showQuotePreviewModal': {
                    showQuotePreviewModal.value = false;
                }
            }
        }

        const $swal = inject('$swal');

        const finishEditing = (publish) => {
            if (publish === true && !props.isTemplate) {
                $swal({
                    title: 'Are you sure you want to publish the quote?',
                    text: "The client will be notified by email that a new quote has been published for him.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, publish the quote!'
                }).then((result) => {
                    if (result.isConfirmed) {
                        saveQuote(true, true);
                    }
                });
            } else {
                saveQuote(false, true);
            }
        }

        const showQuotePreviewModal = ref(false);

        const canBeSubmitted = computed(() => props.quote?.status >= 0 && props.quote?.status !== 1)

        return {
            form,
            addSection,
            sectionTypeWrapper,
            sectionTypeContent,
            saveQuote,
            removeSection,
            cloneSection,
            showBlockTypeSelectModal,
            openBlockTypeModal,
            closeModal,
            templateSelected,
            showBlockPreviewModal,
            previewElement,
            openBlockPreviewModal,
            finishEditing,
            showEditBlockModal,
            editElement,
            openBlockEditModal,
            updateBlock,
            showQuotePreviewModal,
            canBeSubmitted
        }
    }
}
</script>

<style scoped>

</style>
